import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Dialog,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import dayjs from 'dayjs';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import VerifiedIcon from '@mui/icons-material/Verified';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import useVoucher from '../../hooks/useVoucher';
import useVoucherProduct from '../../hooks/useVoucherProduct';
import useBranch from '../../hooks/useBranch';
import AuthContext from '../../context/authContext';
import { toast } from 'react-toastify';

const steps = ['Gutschein konfigurieren', 'Bestätigen', 'Abschluss'];
const DEFAULT_EXPIRATION = dayjs().add(3, 'year').format('YYYY-MM-DD');
const DEFAULT_THRESHOLD = 100;

function statusLabel(status) {
  const s = String(status || '').trim();
  if (s === 'pending') return 'offen';
  if (s === 'approved') return 'freigegeben';
  if (s === 'rejected') return 'abgelehnt';
  return s || '-';
}

function branchLabel(branch) {
  return branch?.longName || branch?.locationName || branch?.name || 'Unbekannte Filiale';
}

function formatEuro(value) {
  return Number(value || 0).toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getMonthlyBudgetErrorMessage(error) {
  const budgetData = error?.data?.data || error?.data || {};
  const remaining = formatEuro(budgetData.remaining);
  const requested = formatEuro(budgetData.requested);
  return `Monatskontingent überschritten: verfügbar ${remaining} €, angefragt ${requested} €.`;
}

function GiftedBalanceVoucherIssue() {
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const {
    issueGiftedBalanceVoucher,
    getGiftedBalanceRedeemLocations,
    getGiftedBalanceVoucherBudget,
    sendVoucherToEmail,
    getVoucherDownloadURL,
    getVouchersZipURL,
    getAllVouchers,
    getGiftedBalanceVoucherRequests,
  } = useVoucher();
  const { getAllVoucherProducts, isLoading: isLoadingProducts } = useVoucherProduct();
  const { isLoading: isLoadingBranches } = useBranch();

  const [activeStep, setActiveStep] = useState(0);
  const [voucherProducts, setVoucherProducts] = useState([]);
  const [selectedVoucherProduct, setSelectedVoucherProduct] = useState('');
  const [branchOptions, setBranchOptions] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  // Optional entertainment within the selected branch ('' = whole branch / all entertainments).
  const [selectedEntertainmentId, setSelectedEntertainmentId] = useState('');
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdVoucher, setCreatedVoucher] = useState(null);
  const [createdVouchers, setCreatedVouchers] = useState([]);
  const [createdRequestId, setCreatedRequestId] = useState(null);
  const [approvalThreshold, setApprovalThreshold] = useState(DEFAULT_THRESHOLD);
  const [monthlyBudget, setMonthlyBudget] = useState(null);
  const [monthlyBudgetLoading, setMonthlyBudgetLoading] = useState(false);

  const amountRef = useRef(null);
  const quantityRef = useRef(null);
  const additionalCommentRef = useRef(null);
  const emailRef = useRef(null);
  const [draftAmount, setDraftAmount] = useState(0);
  const [draftQuantity, setDraftQuantity] = useState(1);
  const [draftComment, setDraftComment] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [sentEmailAddress, setSentEmailAddress] = useState('');
  const [zipUrl, setZipUrl] = useState(null);

  const [recentVouchers, setRecentVouchers] = useState([]);
  const [recentVouchersLoading, setRecentVouchersLoading] = useState(false);
  const [recentRequests, setRecentRequests] = useState([]);
  const [recentRequestsLoading, setRecentRequestsLoading] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailModalVoucher, setEmailModalVoucher] = useState(null);
  const [emailModalSending, setEmailModalSending] = useState(false);
  const [selectedListVoucher, setSelectedListVoucher] = useState(null);
  const [selectedListVoucherPdfUrl, setSelectedListVoucherPdfUrl] = useState(null);

  const listEmailInputRef = useRef(null);

  const selectedProduct = useMemo(
    () => voucherProducts.find(p => p._id === selectedVoucherProduct),
    [voucherProducts, selectedVoucherProduct]
  );

  const selectedBranch = useMemo(
    () => branchOptions.find(branch => String(branch._id) === String(selectedBranchId)),
    [branchOptions, selectedBranchId]
  );

  const selectedBranchEntertainments = useMemo(
    () => (Array.isArray(selectedBranch?.entertainments) ? selectedBranch.entertainments : []),
    [selectedBranch]
  );

  const selectedEntertainment = useMemo(
    () =>
      selectedBranchEntertainments.find(
        ent => String(ent._id) === String(selectedEntertainmentId)
      ),
    [selectedBranchEntertainments, selectedEntertainmentId]
  );

  // Combined "redeemable in" options: one "Alle Entertainments" entry per branch
  // plus one entry per entertainment within that branch.
  const redemptionOptions = useMemo(() => {
    const options = [];
    branchOptions.forEach(branch => {
      const base = branchLabel(branch);
      options.push({
        key: `${branch._id}::`,
        branchId: String(branch._id),
        entertainmentId: '',
        label: `${base} – Alle Entertainments`,
      });
      (Array.isArray(branch.entertainments) ? branch.entertainments : []).forEach(ent => {
        options.push({
          key: `${branch._id}::${ent._id}`,
          branchId: String(branch._id),
          entertainmentId: String(ent._id),
          label: `${base} – ${ent.longName}`,
        });
      });
    });
    return options;
  }, [branchOptions]);

  const selectedRedemptionKey = selectedBranchId
    ? `${selectedBranchId}::${selectedEntertainmentId || ''}`
    : '';

  const redemptionLabel = useMemo(() => {
    if (!selectedBranch) return '';
    const base = branchLabel(selectedBranch);
    return selectedEntertainment
      ? `${base} – ${selectedEntertainment.longName}`
      : `${base} – Alle Entertainments`;
  }, [selectedBranch, selectedEntertainment]);

  const handleRedemptionChange = key => {
    const [branchPart, entertainmentPart = ''] = String(key).split('::');
    setSelectedBranchId(branchPart || '');
    setSelectedEntertainmentId(entertainmentPart || '');
  };

  const loadMonthlyBudget = async branchId => {
    if (!branchId) {
      setMonthlyBudget(null);
      return;
    }

    setMonthlyBudgetLoading(true);
    try {
      const summary = await getGiftedBalanceVoucherBudget({
        branchId,
        year: dayjs().year(),
        month: dayjs().month() + 1,
      });
      setMonthlyBudget(summary || null);
    } catch (error) {
      setMonthlyBudget(null);
      toast.error('Fehler beim Laden des Monatskontingents');
    } finally {
      setMonthlyBudgetLoading(false);
    }
  };

  const commentPrefix = useMemo(() => {
    const userLabel = authCtx.currentUser?.userName || 'Mitarbeiter';
    return `Arena: Kulanz-/Lokalmarketing-Gutschein ausgestellt durch ${userLabel}`;
  }, [authCtx.currentUser?.userName]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const products = await getAllVoucherProducts();
        setVoucherProducts(products || []);
      } catch (error) {
        toast.error('Fehler beim Laden der Gutscheinprodukte');
      }
    };
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchRedeemLocations = async () => {
      setBranchesLoading(true);
      try {
        const result = await getGiftedBalanceRedeemLocations();
        const branches = Array.isArray(result?.branches) ? result.branches : [];
        setBranchOptions(branches);

        if (branches.length === 1) {
          setSelectedBranchId(branches[0]._id);
        } else {
          setSelectedBranchId(prev =>
            branches.some(branch => String(branch._id) === String(prev)) ? prev : ''
          );
        }
      } catch (error) {
        setBranchOptions([]);
        setSelectedBranchId('');
        toast.error('Fehler beim Laden der Einlöseorte');
      } finally {
        setBranchesLoading(false);
      }
    };

    fetchRedeemLocations();
  }, []);

  useEffect(() => {
    loadMonthlyBudget(selectedBranchId);
  }, [selectedBranchId]);

  const fetchRecentVouchers = async () => {
    setRecentVouchersLoading(true);
    try {
      const yesterday = dayjs().subtract(1, 'day').startOf('day').toISOString();
      const filters = JSON.stringify([{ field: 'createdAt', operator: 'gte', value: yesterday }]);
      const response = await getAllVouchers('', 1, 50, [], true, filters);
      const results = Array.isArray(response?.results) ? response.results : [];
      setRecentVouchers(results.filter(v => v?.kind === 'gifted-balance'));
    } catch (error) {
      setRecentVouchers([]);
    } finally {
      setRecentVouchersLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentVouchers();
  }, []);

  const fetchMyRequests = async () => {
    setRecentRequestsLoading(true);
    try {
      const res = await getGiftedBalanceVoucherRequests('');
      setRecentRequests(Array.isArray(res?.results) ? res.results : []);
    } catch (error) {
      setRecentRequests([]);
    } finally {
      setRecentRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchMyRequests();
  }, []);

  useEffect(() => {
    if (activeStep !== 0) return;
    if (amountRef.current) {
      amountRef.current.value = draftAmount > 0 ? String(draftAmount) : '';
    }
    if (quantityRef.current) {
      quantityRef.current.value = String(draftQuantity || 1);
    }
    if (additionalCommentRef.current) {
      additionalCommentRef.current.value = draftComment || '';
    }
  }, [activeStep, draftAmount, draftQuantity, draftComment]);

  const getEnteredAmount = () => {
    const raw = amountRef.current?.value;
    const value = Number(String(raw || '').replace(',', '.'));
    return Number.isFinite(value) ? Math.round(value * 100) / 100 : 0;
  };

  const buildComment = () => {
    const additional = String(draftComment || '').trim();
    return additional ? `${commentPrefix}: ${additional}` : commentPrefix;
  };

  const getEnteredQuantity = () => {
    const raw = quantityRef.current?.value;
    const value = parseInt(String(raw || '').trim(), 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  };

  const syncDraftFromRefs = () => {
    const amount = getEnteredAmount();
    const quantity = getEnteredQuantity();
    const comment = additionalCommentRef.current?.value?.trim() || '';
    setDraftAmount(amount);
    setDraftQuantity(quantity);
    setDraftComment(comment);
    return { amount, quantity, comment };
  };

  const validateStep1 = () => {
    const { amount, quantity } = syncDraftFromRefs();
    if (!selectedVoucherProduct) {
      toast.error('Bitte ein Gutscheinprodukt auswählen');
      return false;
    }
    if (!selectedBranchId) {
      toast.error('Bitte eine Filiale für die Einlösbarkeit auswählen');
      return false;
    }
    if (amount <= 0) {
      toast.error('Bitte einen gültigen Gutscheinwert eingeben');
      return false;
    }
    if (quantity < 1 || quantity > 200) {
      toast.error('Bitte eine gültige Quantität (1–200) eingeben');
      return false;
    }
    // Exceeding the monthly budget no longer blocks the issuance; it is routed
    // into the manager approval flow (see needsApproval), so we let it proceed.
    return true;
  };

  const validateStep2 = () => {
    if (!confirmed) {
      toast.error('Bitte bestätige die Ausgabe');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      if (validateStep1()) setActiveStep(1);
      return;
    }
    if (activeStep === 1) {
      if (!validateStep2()) return;
      await submit();
    }
  };

  const handleBack = () => {
    setActiveStep(prev => Math.max(0, prev - 1));
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedVoucherProduct('');
    setSelectedBranchId(branchOptions.length === 1 ? branchOptions[0]._id : '');
    setSelectedEntertainmentId('');
    if (amountRef.current) amountRef.current.value = '';
    if (quantityRef.current) quantityRef.current.value = '1';
    if (additionalCommentRef.current) additionalCommentRef.current.value = '';
    if (emailRef.current) emailRef.current.value = '';
    setDraftAmount(0);
    setDraftQuantity(1);
    setDraftComment('');
    setConfirmed(false);
    setCreatedVoucher(null);
    setCreatedVouchers([]);
    setCreatedRequestId(null);
    setSentEmailAddress('');
    setZipUrl(null);
    fetchRecentVouchers();
    fetchMyRequests();
  };

  const submit = async () => {
    setIsSubmitting(true);
    try {
      const amount = draftAmount > 0 ? draftAmount : syncDraftFromRefs().amount;
      const quantity = draftQuantity > 0 ? draftQuantity : syncDraftFromRefs().quantity;
      const res = await issueGiftedBalanceVoucher({
        voucherValue: amount,
        quantity,
        relatedVoucherProductId: selectedVoucherProduct,
        branchId: selectedBranchId,
        entertainmentId: selectedEntertainmentId || undefined,
        expirationDate: DEFAULT_EXPIRATION,
        creationComment: buildComment(),
      });

      if (res?.threshold) {
        setApprovalThreshold(res.threshold);
      }
      if (res?.monthlyBudget) {
        setMonthlyBudget({
          ...res.monthlyBudget,
          remaining: res.monthlyBudget.remainingAfterRequest ?? res.monthlyBudget.remaining,
        });
      }

      if (res?.requiresApproval) {
        setCreatedRequestId(res.requestId);
        setActiveStep(2);
        toast.success('Freigabeanfrage wurde eingereicht');
        fetchRecentVouchers();
        fetchMyRequests();
        return;
      }

      if (Array.isArray(res?.vouchers) && res.vouchers.length > 0) {
        setCreatedVouchers(res.vouchers);
        setCreatedVoucher(res.vouchers[0]);
        try {
          const ids = res.vouchers.map(v => v.voucherId).filter(Boolean);
          if (ids.length > 1) {
            const zip = await getVouchersZipURL(ids);
            setZipUrl(zip?.preSignedURL || null);
          }
        } catch (_) {
          setZipUrl(null);
        }
      } else {
        setCreatedVouchers([]);
        setCreatedVoucher(res);
      }
      setActiveStep(2);
      toast.success(
        Array.isArray(res?.vouchers) && res.vouchers.length > 1
          ? `${res.vouchers.length} Gutscheine erfolgreich erstellt!`
          : 'Gutschein erfolgreich erstellt!'
      );
      fetchRecentVouchers();
      fetchMyRequests();
    } catch (error) {
      if (error?.code === 'GIFTED_VOUCHER_MONTHLY_BUDGET_EXCEEDED') {
        toast.error(getMonthlyBudgetErrorMessage(error));
      } else {
        toast.error('Fehler beim Erstellen des Kulanz-/Marketing-Gutscheins');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Build a download URL for the freshly created voucher with the requested
  // content disposition (inline = view, attachment = download).
  const buildCreatedVoucherPdfUrl = async disposition => {
    if (!createdVoucher?.voucherId) return null;
    const response = await getVoucherDownloadURL(createdVoucher.voucherId);
    if (!response?.preSignedURL) return null;
    const url = response.preSignedURL;
    return `${url}${url.includes('?') ? '&' : '?'}disposition=${disposition}`;
  };

  // Download the voucher PDF (attachment disposition forces a file download).
  const handleDownloadPdf = async () => {
    try {
      const downloadUrl = await buildCreatedVoucherPdfUrl('attachment');
      if (!downloadUrl) return;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      toast.error('Fehler beim Herunterladen des Gutscheins');
    }
  };

  // Open the voucher PDF in the in-app popup viewer (inline, no download).
  const handleOpenPdfInPopup = async () => {
    try {
      const inlineUrl = await buildCreatedVoucherPdfUrl('inline');
      if (!inlineUrl) return;
      setSelectedListVoucher(createdVoucher);
      setSelectedListVoucherPdfUrl(inlineUrl);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Fehler beim Laden des Gutscheins');
    }
  };

  // Open the voucher PDF inline in a separate browser window.
  const handleOpenPdfInNewWindow = async () => {
    try {
      const inlineUrl = await buildCreatedVoucherPdfUrl('inline');
      if (!inlineUrl) return;
      window.open(inlineUrl, '_blank', 'noopener,width=900,height=1100');
    } catch (error) {
      toast.error('Fehler beim Laden des Gutscheins');
    }
  };

  const handleSendEmail = async () => {
    const email = emailRef.current?.value?.trim();
    if (!email || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }
    if (!createdVoucher?.voucherId) return;

    setEmailSending(true);
    try {
      if (createdVouchers.length > 0) {
        for (const v of createdVouchers) {
          if (!v?.voucherId) continue;
          await sendVoucherToEmail(v.voucherId, email);
        }
      } else {
        await sendVoucherToEmail(createdVoucher.voucherId, email);
      }
      setSentEmailAddress(email);
      toast.success('E-Mail erfolgreich gesendet!');
    } catch (error) {
      toast.error('Fehler beim Senden der E-Mail');
    } finally {
      setEmailSending(false);
    }
  };

  const handleOpenListVoucherPdf = async voucher => {
    try {
      const response = await getVoucherDownloadURL(voucher._id);
      if (response?.preSignedURL) {
        const url = response.preSignedURL;
        const inlineUrl = `${url}${url.includes('?') ? '&' : '?'}disposition=inline`;
        setSelectedListVoucher(voucher);
        setSelectedListVoucherPdfUrl(inlineUrl);
        setPdfViewerOpen(true);
      }
    } catch (error) {
      toast.error('Fehler beim Laden des Gutscheins');
    }
  };

  const handleClosePdfViewer = () => {
    setPdfViewerOpen(false);
    setSelectedListVoucher(null);
    setSelectedListVoucherPdfUrl(null);
  };

  const handleOpenEmailModal = voucher => {
    setEmailModalVoucher(voucher);
    setEmailModalOpen(true);
    if (listEmailInputRef.current) listEmailInputRef.current.value = '';
  };

  const handleCloseEmailModal = () => {
    setEmailModalOpen(false);
    setEmailModalVoucher(null);
    if (listEmailInputRef.current) listEmailInputRef.current.value = '';
  };

  const handleSendListEmail = async () => {
    const email = listEmailInputRef.current?.value?.trim();
    if (!email || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein');
      return;
    }
    if (!emailModalVoucher?._id) return;

    setEmailModalSending(true);
    try {
      await sendVoucherToEmail(emailModalVoucher._id, email);
      toast.success('E-Mail erfolgreich gesendet!');
      handleCloseEmailModal();
    } catch (error) {
      toast.error('Fehler beim Senden der E-Mail');
    } finally {
      setEmailModalSending(false);
    }
  };

  if (!authCtx.checkRight('voucher:special:giftedBalance')) {
    return <Navigate to='/welcome' replace />;
  }

  const amountPreview = activeStep === 0 ? getEnteredAmount() : draftAmount;
  const quantityPreview = activeStep === 0 ? getEnteredQuantity() : draftQuantity;
  const requestedTotalPreview = Math.round((amountPreview || 0) * (quantityPreview || 1) * 100) / 100;
  const exceedsApprovalThreshold = (amountPreview || 0) > approvalThreshold;
  const exceedsMonthlyBudget = Boolean(
    monthlyBudget && requestedTotalPreview > Number(monthlyBudget.remaining || 0)
  );
  // Exceeding either the per-voucher threshold or the monthly budget routes the
  // issuance into the manager approval flow instead of rejecting it.
  const needsApproval = exceedsApprovalThreshold || exceedsMonthlyBudget;

  return (
    <Box sx={{ ...viewRootTextPrimarySx, p: { xs: 2, md: 4 }, maxWidth: 800, mx: 'auto' }}>
      <Stepper activeStep={activeStep} sx={{ mb: 5 }} alternativeLabel>
        {steps.map(label => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <CardGiftcardIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant='h5' fontWeight='bold'>
                Kulanz-/Lokalmarketing-Gutschein
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Gutschein als Geschenk
              </Typography>
            </Box>
          </Box>

          <Stack spacing={3}>
            <FormControl fullWidth disabled={isLoadingProducts}>
              <InputLabel>Gutscheinprodukt *</InputLabel>
              <Select
                value={selectedVoucherProduct}
                label='Gutscheinprodukt *'
                onChange={e => setSelectedVoucherProduct(e.target.value)}
              >
                {voucherProducts.map(product => (
                  <MenuItem key={product._id} value={product._id}>
                    {product.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <FormControl
                fullWidth
                disabled={branchesLoading || isLoadingBranches || redemptionOptions.length === 0}
              >
                <InputLabel>Einlösbar in *</InputLabel>
                <Select
                  value={selectedRedemptionKey}
                  label='Einlösbar in *'
                  onChange={e => handleRedemptionChange(e.target.value)}
                >
                  {redemptionOptions.map(option => (
                    <MenuItem key={option.key} value={option.key}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{ display: 'block', mt: 0.75 }}
              >
                Wähle die Filiale (alle Entertainments) oder ein bestimmtes Entertainment, in dem
                der Gutschein einlösbar ist.
              </Typography>
            </Box>

            {redemptionOptions.length === 0 && !branchesLoading && !isLoadingBranches && (
              <Alert severity='warning'>
                Es sind keine Einlöseorte für die Auswahl verfügbar. Bitte prüfe die
                Filialzuordnung oder Berechtigungen.
              </Alert>
            )}

            {selectedBranchId && (
              <Alert
                severity={exceedsMonthlyBudget ? 'warning' : 'info'}
                icon={monthlyBudgetLoading ? <CircularProgress size={20} /> : undefined}
              >
                {monthlyBudgetLoading || !monthlyBudget ? (
                  'Monatskontingent wird geladen ...'
                ) : (
                  <>
                    Monatskontingent {String(monthlyBudget.month).padStart(2, '0')}/
                    {monthlyBudget.year}: <strong>{formatEuro(monthlyBudget.remaining)} €</strong>{' '}
                    verfügbar von {formatEuro(monthlyBudget.budget)} €.
                    {Number(monthlyBudget.pending || 0) > 0
                      ? ` Offene Anfragen reservieren ${formatEuro(monthlyBudget.pending)} €.`
                      : ''}
                    {exceedsMonthlyBudget
                      ? ` Angefragter Gesamtwert: ${formatEuro(requestedTotalPreview)} €.`
                      : ''}
                  </>
                )}
              </Alert>
            )}

            <TextField
              inputRef={amountRef}
              label='Gutscheinwert *'
              type='number'
              defaultValue={draftAmount > 0 ? String(draftAmount) : ''}
              onBlur={() => {
                const { amount } = syncDraftFromRefs();
                if (amount > 0 && amountRef.current) {
                  amountRef.current.value = String(amount);
                }
              }}
              InputProps={{ startAdornment: <InputAdornment position='start'>€</InputAdornment> }}
              inputProps={{ min: 0.01, max: 2000, step: 0.01 }}
              helperText={`Bis ${approvalThreshold}€ ohne Freigabe`}
            />

            <TextField
              inputRef={quantityRef}
              label='Quantität *'
              type='number'
              defaultValue={String(draftQuantity || 1)}
              onBlur={() => {
                const { quantity } = syncDraftFromRefs();
                if (quantityRef.current) {
                  quantityRef.current.value = String(quantity);
                }
              }}
              inputProps={{ min: 1, max: 200, step: 1 }}
              helperText='Anzahl der zu erstellenden Gutscheine (1–200)'
            />

            {needsApproval ? (
              <Alert severity='warning' icon={<AccountTreeIcon />}>
                {exceedsApprovalThreshold
                  ? `Betrag > ${approvalThreshold}€`
                  : 'Monatskontingent überschritten'}{' '}
                – es wird eine Manager-Freigabe benötigt.
              </Alert>
            ) : (
              <Alert severity='info' icon={<VerifiedIcon />}>
                Betrag ≤ {approvalThreshold}€ – kann direkt ausgestellt werden.
              </Alert>
            )}

            {selectedProduct && (
              <Alert severity='info'>
                Produkt: <strong>{selectedProduct.name}</strong>
              </Alert>
            )}

            <Box>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                Kommentar
              </Typography>
              <Typography variant='body1' sx={{ mb: 1 }}>
                {commentPrefix}
              </Typography>
              <TextField
                placeholder='Zusätzlicher Kommentar (optional)'
                multiline
                rows={2}
                inputRef={additionalCommentRef}
                fullWidth
                size='small'
                onBlur={() => {
                  const value = additionalCommentRef.current?.value?.trim() || '';
                  setDraftComment(value);
                }}
              />
            </Box>
          </Stack>
        </Box>
      )}

      {activeStep === 1 && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <VerifiedIcon sx={{ fontSize: 40, color: 'warning.main' }} />
            <Box>
              <Typography variant='h5' fontWeight='bold'>
                Ausgabe bestätigen
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Bitte prüfen und bestätigen
              </Typography>
            </Box>
          </Box>

          <Card variant='outlined' sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Zusammenfassung
              </Typography>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Produkt:</Typography>
                  <Typography>{selectedProduct?.name || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Einlösbar in:</Typography>
                  <Typography>{redemptionLabel || '-'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Wert:</Typography>
                  <Typography fontWeight='bold'>
                    {amountPreview.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    €
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Quantität:</Typography>
                  <Typography fontWeight='bold'>{quantityPreview}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Gesamtwert:</Typography>
                  <Typography fontWeight='bold'>{formatEuro(requestedTotalPreview)} €</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography color='text.secondary'>Gültig bis:</Typography>
                  <Typography>{dayjs(DEFAULT_EXPIRATION).format('DD.MM.YYYY')}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {needsApproval ? (
            <Alert severity='warning' sx={{ mb: 2 }}>
              Diese Ausgabe wird als <strong>Freigabeanfrage</strong> eingereicht.
            </Alert>
          ) : (
            <Alert severity='info' sx={{ mb: 2 }}>
              Diese Ausgabe wird <strong>sofort</strong> als Gutschein erstellt.
            </Alert>
          )}

          {monthlyBudget && (
            <Alert severity={exceedsMonthlyBudget ? 'warning' : 'info'} sx={{ mb: 2 }}>
              Verfügbares Monatskontingent: <strong>{formatEuro(monthlyBudget.remaining)} €</strong>.
              Angefragter Gesamtwert: <strong>{formatEuro(requestedTotalPreview)} €</strong>.
              {exceedsMonthlyBudget
                ? ' Das Monatskontingent wird überschritten – die Ausgabe wird zur Freigabe eingereicht.'
                : ''}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
            }
            label='Ich bestätige die Ausgabe des Kulanz-/Lokalmarketing-Gutscheins'
          />
        </Box>
      )}

      {activeStep === 2 && (
        <Box sx={{ textAlign: 'center' }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          {createdRequestId ? (
            <>
              <Typography variant='h4' fontWeight='bold' gutterBottom>
                Freigabe angefragt
              </Typography>
              <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
                Die Anfrage wurde gespeichert und muss von einem Manager freigegeben werden.
              </Typography>
              <Alert severity='info' sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
                Request-ID: <strong>{createdRequestId}</strong>
              </Alert>
              <Button variant='text' onClick={handleReset} sx={{ textTransform: 'none' }}>
                Weitere Anfrage erstellen
              </Button>
            </>
          ) : (
            <>
              <Typography variant='h4' fontWeight='bold' gutterBottom>
                {createdVouchers.length > 1 ? 'Gutscheine erstellt!' : 'Gutschein erstellt!'}
              </Typography>
              <Typography variant='body1' color='text.secondary' sx={{ mb: 4 }}>
                Der Gutschein wurde als Geschenk (GIFTEDBALANCE) angelegt und ist sofort gültig.
              </Typography>

              <Card
                elevation={3}
                sx={{
                  maxWidth: 420,
                  mx: 'auto',
                  mb: 4,
                  backgroundColor: theme.palette.primary.main,
                  backgroundImage: 'none',
                  color: theme.palette.primary.contrastText,
                  borderRadius: 3,
                }}
              >
                <CardContent sx={{ py: 4 }}>
                  <CardGiftcardIcon sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                  <Typography variant='h3' fontWeight='bold' gutterBottom>
                    {amountPreview.toLocaleString('de-DE', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    €
                  </Typography>
                  <Typography variant='h6' sx={{ opacity: 0.9, letterSpacing: 2 }}>
                    {createdVouchers.length > 1
                      ? `${createdVouchers.length} Gutscheine`
                      : createdVoucher?.voucherCode}
                  </Typography>
                  <Typography variant='body2' sx={{ opacity: 0.8, mt: 2 }}>
                    Gültig bis {dayjs(DEFAULT_EXPIRATION).format('DD.MM.YYYY')}
                  </Typography>
                </CardContent>
              </Card>

              {createdVouchers.length > 1 && (
                <Box sx={{ maxWidth: 720, mx: 'auto', mb: 3 }}>
                  <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
                    Erstellte Gutscheine
                  </Typography>
                  <TableContainer>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Code</TableCell>
                          <TableCell align='right'>Wert</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {createdVouchers.map(v => (
                          <TableRow key={v.voucherId || v.voucherCode}>
                            <TableCell>
                              <Typography variant='body2' fontFamily='monospace'>
                                {v.voucherCode}
                              </Typography>
                            </TableCell>
                            <TableCell align='right'>
                              {Number(v.voucherValue || 0).toLocaleString('de-DE', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              €
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent='center'
                sx={{ mb: 3 }}
              >
                <Button
                  variant='contained'
                  size='large'
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPdf}
                  sx={{ textTransform: 'none' }}
                >
                  Herunterladen
                </Button>
                <Button
                  variant='outlined'
                  size='large'
                  startIcon={<VisibilityIcon />}
                  onClick={handleOpenPdfInPopup}
                  sx={{ textTransform: 'none' }}
                >
                  In Pop-up öffnen
                </Button>
                <Button
                  variant='outlined'
                  size='large'
                  startIcon={<OpenInNewIcon />}
                  onClick={handleOpenPdfInNewWindow}
                  sx={{ textTransform: 'none' }}
                >
                  In neuem Fenster öffnen
                </Button>
                {zipUrl && (
                  <Button
                    variant='contained'
                    size='large'
                    onClick={() => window.open(zipUrl, '_blank')}
                    sx={{ textTransform: 'none' }}
                  >
                    ZIP (alle PDFs)
                  </Button>
                )}
              </Stack>

              <Box sx={{ maxWidth: 520, mx: 'auto', mb: 3 }}>
                {sentEmailAddress ? (
                  <Alert severity='success'>
                    E-Mail wurde erfolgreich an <strong>{sentEmailAddress}</strong> gesendet!
                  </Alert>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <TextField
                      inputRef={emailRef}
                      label='E-Mail-Adresse (optional)'
                      type='email'
                      fullWidth
                      size='small'
                      placeholder='kunde@example.com'
                    />
                    <Button
                      variant='contained'
                      onClick={handleSendEmail}
                      disabled={emailSending}
                      startIcon={emailSending ? <CircularProgress size={20} /> : <EmailIcon />}
                      sx={{ minWidth: 140 }}
                    >
                      {emailSending ? 'Senden...' : 'Senden'}
                    </Button>
                  </Box>
                )}
              </Box>

              <Button variant='text' onClick={handleReset} sx={{ textTransform: 'none' }}>
                Weiteren Gutschein erstellen
              </Button>
            </>
          )}
        </Box>
      )}

      {activeStep < 2 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: activeStep === 0 ? 'flex-end' : 'space-between',
            mt: 4,
          }}
        >
          {activeStep > 0 && (
            <Button onClick={handleBack} sx={{ textTransform: 'none' }}>
              Zurück
            </Button>
          )}
          <Button
            variant='contained'
            onClick={handleNext}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{ textTransform: 'none' }}
          >
            {isSubmitting ? 'Wird verarbeitet...' : activeStep === 1 ? 'Ausgabe abschließen' : 'Weiter'}
          </Button>
        </Box>
      )}

      <Accordion sx={{ mt: 6 }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight='medium'>Zuletzt von mir erstellte Kulanz-/Marketing-Gutscheine</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {recentVouchersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentVouchers.length === 0 ? (
            <Typography color='text.secondary'>Keine Gutscheine in den letzten 2 Tagen erstellt.</Typography>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Code</TableCell>
                    <TableCell>Wert</TableCell>
                    <TableCell>Erstellt am</TableCell>
                    <TableCell>Kommentar</TableCell>
                    <TableCell align='right'>Aktion</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentVouchers.map(voucher => (
                    <TableRow key={voucher._id}>
                      <TableCell>
                        <Typography variant='body2' fontFamily='monospace'>
                          {voucher.voucherCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {voucher.initialBalance?.sum?.toLocaleString('de-DE', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        €
                      </TableCell>
                      <TableCell>{dayjs(voucher.meta?.createdAt).format('DD.MM.YYYY HH:mm')}</TableCell>
                      <TableCell>
                        <Tooltip title={voucher.creationComment || '-'} arrow>
                          <Typography
                            variant='body2'
                            noWrap
                            sx={{ maxWidth: 200, cursor: 'default' }}
                          >
                            {voucher.creationComment || '-'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align='right'>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenListVoucherPdf(voucher)}
                          title='PDF anzeigen'
                        >
                          <VisibilityIcon fontSize='small' />
                        </IconButton>
                        <IconButton
                          size='small'
                          onClick={() => handleOpenEmailModal(voucher)}
                          title='Per E-Mail senden'
                        >
                          <EmailIcon fontSize='small' />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mt: 2 }} defaultExpanded={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight='medium'>Meine Freigabeanfragen</Typography>
        </AccordionSummary>
        <AccordionDetails>
          {recentRequestsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : recentRequests.length === 0 ? (
            <Typography color='text.secondary'>Keine Freigabeanfragen vorhanden.</Typography>
          ) : (
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Datum</TableCell>
                    <TableCell>Filiale</TableCell>
                    <TableCell>Produkt</TableCell>
                    <TableCell align='right'>Wert</TableCell>
                    <TableCell>Antrag</TableCell>
                    <TableCell>Ablehnungsgrund</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recentRequests.map(r => {
                    const requestedAt = r.meta?.requestedAt
                      ? dayjs(r.meta.requestedAt).format('DD.MM.YYYY HH:mm')
                      : '-';
                    const branchName =
                      r.branchId?.longName ||
                      r.relatedVoucherProductId?.branchId?.longName ||
                      r.branchId?.locationName ||
                      '-';
                    const productName = r.relatedVoucherProductId?.name || '-';
                    const amount = Number(r.amount || 0) || 0;
                    const requestComment = r.requestComment || r.reason || '-';
                    const decisionComment = r.decisionComment || '-';

                    return (
                      <TableRow key={r._id}>
                        <TableCell>{statusLabel(r.status)}</TableCell>
                        <TableCell>{requestedAt}</TableCell>
                        <TableCell>{branchName}</TableCell>
                        <TableCell>{productName}</TableCell>
                        <TableCell align='right'>
                          {amount.toLocaleString('de-DE', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          €
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 240,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={requestComment}
                        >
                          {requestComment}
                        </TableCell>
                        <TableCell
                          sx={{
                            maxWidth: 240,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={decisionComment}
                        >
                          {r.status === 'rejected' ? decisionComment : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={pdfViewerOpen}
        onClose={handleClosePdfViewer}
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { height: '85vh' } }}
      >
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Gutschein {selectedListVoucher?.voucherCode}
            </Typography>
            <IconButton edge='end' color='inherit' onClick={handleClosePdfViewer}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 0, height: 'calc(85vh - 64px)' }}>
          {selectedListVoucherPdfUrl && (
            <iframe
              src={`${selectedListVoucherPdfUrl}#toolbar=0&navpanes=0`}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title='Gutschein PDF'
            />
          )}
        </Box>
      </Dialog>

      <Dialog open={emailModalOpen} onClose={handleCloseEmailModal} maxWidth='sm' fullWidth>
        <AppBar position='static' color='default' elevation={1}>
          <Toolbar>
            <Typography variant='h6' component='div' sx={{ flexGrow: 1 }}>
              Gutschein per E-Mail senden
            </Typography>
            <IconButton edge='end' color='inherit' onClick={handleCloseEmailModal}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3 }}>
          <Typography variant='body2' sx={{ mb: 2 }}>
            Gutschein <strong>{emailModalVoucher?.voucherCode}</strong> an folgende E-Mail-Adresse senden:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              inputRef={listEmailInputRef}
              label='E-Mail-Adresse'
              type='email'
              fullWidth
              size='small'
              placeholder='kunde@example.com'
              autoFocus
            />
            <Button
              variant='contained'
              onClick={handleSendListEmail}
              disabled={emailModalSending}
              startIcon={emailModalSending ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{ minWidth: 120 }}
            >
              {emailModalSending ? 'Senden...' : 'Senden'}
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default GiftedBalanceVoucherIssue;

